import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import EditModalFooter from './EditModalFooter'
import type { PatchRestaurantBarItemViewModel } from './models'
import {
  extractEditableStartDateTimeStrings,
  updateRestaurantBarPayloadFromViewModel,
} from './utils'
import CustomThingToDoFields from '../Common/CustomThingToDoFields'
import { formOptions } from '../Common/Fields/config'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { RestaurantBarItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: RestaurantBarItem
}

export const itemToView = (
  item: RestaurantBarItem,
): PatchRestaurantBarItemViewModel => {
  return {
    type: item.type,
    name: item.name,
    price: item.price,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    ...extractEditableStartDateTimeStrings(item),
    place: item.place,
  }
}

function EditRestaurantBar(props: Props) {
  const { item } = props
  const intl = useIntl()

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<PatchRestaurantBarItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    (viewModel: PatchRestaurantBarItemViewModel) => {
      updateMutate({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: updateRestaurantBarPayloadFromViewModel(viewModel),
      })
    },
    [item, updateMutate],
  )

  const modalTitle = intl.formatMessage({
    id: 'G8n7jIvgociX3lVhRwsa',
    defaultMessage: 'Edit Restaurant & Bar',
    description: 'Modal title when editing a restaurant or bar item in the trip planner',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <CustomThingToDoFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditRestaurantBar
